// src/components/FieldAccessSettings.js
import React, { useEffect, useState } from "react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../lib/firebase";

export default function FieldAccessSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [pins, setPins] = useState({
    responderPin: "",
    hqPin: "",
    dispatchPin: "",
    commandPin: "",
  });

  useEffect(() => {
    loadSettings();
  }, []);

  async function loadSettings() {
    try {
      setLoading(true);
      setMessage("");

      const ref = doc(db, "appConfig", "general");
      const snap = await getDoc(ref);

      if (snap.exists()) {
        const data = snap.data();
        setPins({
          responderPin: data?.fieldAccess?.responderPin || "",
          hqPin: data?.fieldAccess?.hqPin || "",
          dispatchPin: data?.fieldAccess?.dispatchPin || "",
          commandPin: data?.fieldAccess?.commandPin || "",
        });
      }
    } catch (error) {
      console.error("Error loading field access settings:", error);
      setMessage("Failed to load field access settings.");
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    try {
      setSaving(true);
      setMessage("");

      const ref = doc(db, "appConfig", "general");

      await setDoc(
        ref,
        {
          fieldAccess: {
            responderPin: pins.responderPin.trim(),
            hqPin: pins.hqPin.trim(),
            dispatchPin: pins.dispatchPin.trim(),
            commandPin: pins.commandPin.trim(),
          },
        },
        { merge: true }
      );

      setMessage("Field access PINs saved.");
    } catch (error) {
      console.error("Error saving field access settings:", error);
      setMessage("Failed to save field access settings.");
    } finally {
      setSaving(false);
    }
  }

  const sectionStyle = {
    background: "#fff",
    border: "1px solid #d9e2ec",
    borderRadius: 12,
    padding: 16,
    marginTop: 20,
  };

  const titleStyle = {
    margin: "0 0 8px 0",
    fontSize: 18,
    fontWeight: 700,
    color: "#0f172a",
  };

  const hintStyle = {
    margin: "0 0 16px 0",
    color: "#475569",
    fontSize: 14,
  };

  const gridStyle = {
    display: "grid",
    gridTemplateColumns: "180px minmax(180px, 320px)",
    gap: 12,
    alignItems: "center",
  };

  const labelStyle = {
    fontWeight: 600,
    color: "#0f172a",
  };

  const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 8,
    border: "1px solid #cbd5e1",
    fontSize: 16,
    boxSizing: "border-box",
  };

  const buttonStyle = {
    marginTop: 16,
    padding: "10px 16px",
    border: "none",
    borderRadius: 8,
    background: "#0f766e",
    color: "#fff",
    fontWeight: 700,
    cursor: saving ? "not-allowed" : "pointer",
    opacity: saving ? 0.7 : 1,
  };

  if (loading) {
    return (
      <div style={sectionStyle}>
        <div style={titleStyle}>Field Access Settings</div>
        <div style={hintStyle}>Loading field access settings...</div>
      </div>
    );
  }

  return (
    <div style={sectionStyle}>
      <div style={titleStyle}>Field Access Settings</div>
      <div style={hintStyle}>
        Set the PINs used for Responder, HQ, and Dispatch access.
      </div>

      <div style={gridStyle}>
        <label style={labelStyle}>Responder PIN</label>
        <input
          type="text"
          value={pins.responderPin}
          onChange={(e) =>
            setPins((prev) => ({ ...prev, responderPin: e.target.value }))
          }
          style={inputStyle}
        />

        <label style={labelStyle}>HQ PIN</label>
        <input
          type="text"
          value={pins.hqPin}
          onChange={(e) =>
            setPins((prev) => ({ ...prev, hqPin: e.target.value }))
          }
          style={inputStyle}
        />

        <label style={labelStyle}>Dispatch PIN</label>
        <input
          type="text"
          value={pins.dispatchPin}
          onChange={(e) =>
            setPins((prev) => ({ ...prev, dispatchPin: e.target.value }))
          }
          style={inputStyle}
        />

        <label style={labelStyle}>Command PIN</label>
        <input
          type="text"
          value={pins.commandPin}
          onChange={(e) =>
            setPins((prev) => ({ ...prev, commandPin: e.target.value }))
          }
          style={inputStyle}
        />
      </div>

      <button onClick={handleSave} style={buttonStyle} disabled={saving}>
        {saving ? "Saving..." : "Save Changes"}
      </button>

      {!!message && (
        <div
          style={{
            marginTop: 12,
            fontWeight: 600,
            color: message.includes("Failed") ? "#b91c1c" : "#166534",
          }}
        >
          {message}
        </div>
      )}
    </div>
  );
}